import React, { useState, useMemo } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import {
  CommandLineIcon,
  DocumentDuplicateIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "src/helpers/classNames";
import Button from "src/components/Shared/Buttons/Button";
import moment from "moment";

export const CustomAppLogs = ({ data }) => {
  const [logView, setLogView] = useState("timeline"); // "timeline" | "terminal"
  const [search, setSearch] = useState("");

  const basicLogs = data?.basicLogs || [];
  const rawLogs = data?.logs || [];

  const filteredRawLogs = useMemo(() => {
    if (!search.trim()) return rawLogs;
    const q = search.toLowerCase();
    return rawLogs.filter((l) => (l.text || "").toLowerCase().includes(q));
  }, [rawLogs, search]);

  const allLogsText = useMemo(() => {
    return rawLogs
      .map(
        (l) =>
          `[${l.t ? moment(l.t).format("HH:mm:ss") : "--:--:--"}] [${l.stream || "log"}] ${l.text || ""}`
      )
      .join("\n");
  }, [rawLogs]);

  return (
    <div className="animate-fade-in space-y-4 pt-2">
      {/* Sleek Segmented Control Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-2xs">
        {/* Segmented Switcher */}
        <div className="inline-flex items-center rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setLogView("timeline")}
            className={classNames(
              "inline-flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all",
              logView === "timeline"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            <ListBulletIcon className="h-4 w-4" />
            <span>Step Timeline</span>
            <span
              className={classNames(
                "rounded-full px-2 py-0.5 text-[10px]",
                logView === "timeline"
                  ? "bg-indigo-50 text-indigo-600"
                  : "bg-slate-200/60 text-slate-500"
              )}
            >
              {basicLogs.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setLogView("terminal")}
            className={classNames(
              "inline-flex items-center gap-2 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all",
              logView === "terminal"
                ? "bg-gray-900 text-emerald-400 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
          >
            <CommandLineIcon className="h-4 w-4" />
            <span>Console Stream</span>
            <span
              className={classNames(
                "rounded-full px-2 py-0.5 text-[10px]",
                logView === "terminal"
                  ? "bg-gray-800 text-emerald-300"
                  : "bg-slate-200/60 text-slate-500"
              )}
            >
              {rawLogs.length}
            </span>
          </button>
        </div>

        {/* Toolbar Controls */}
        <div className="flex items-center gap-2.5">
          {logView === "terminal" && (
            <div className="relative">
              <input
                type="text"
                placeholder="Search console logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-44 sm:w-60 rounded-xl border border-gray-200 bg-slate-50/50 pl-8 pr-3 text-xs text-gray-800 placeholder-gray-400 focus:bg-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          )}

          {rawLogs.length > 0 && (
            <CopyToClipboard
              text={allLogsText}
              onCopy={() => toast.success("Logs copied to clipboard")}
            >
              <Button
                type="button"
                version="default"
                className="flex !h-9 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 hover:border-gray-300"
                title="Copy all raw logs"
              >
                <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                <span>Copy Logs</span>
              </Button>
            </CopyToClipboard>
          )}
        </div>
      </div>

      {/* View 1: Step Timeline */}
      {logView === "timeline" && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-xs">
          {basicLogs.length > 0 ? (
            <div className="relative pl-7 before:absolute before:bottom-3 before:left-3 before:top-3 before:w-0.5 before:bg-gradient-to-b before:from-indigo-500 before:via-slate-200 before:to-slate-200 space-y-4">
              {basicLogs.map((log, idx) => {
                const isSuccess = log.kind === "success";
                const isWarning = log.kind === "warning";
                const isError = log.kind === "error";

                return (
                  <div key={idx} className="relative flex items-start gap-3.5 group">
                    {/* Timeline Node Icon */}
                    <span
                      className={classNames(
                        "absolute -left-7 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white ring-2 shadow-2xs transition-transform group-hover:scale-110",
                        isSuccess
                          ? "ring-emerald-500 text-emerald-600 bg-emerald-50/50"
                          : isError
                          ? "ring-rose-500 text-rose-600 bg-rose-50/50"
                          : isWarning
                          ? "ring-amber-500 text-amber-600 bg-amber-50/50"
                          : "ring-indigo-500 text-indigo-600 bg-indigo-50/50"
                      )}
                    >
                      {isSuccess ? (
                        <CheckCircleIcon className="h-4 w-4" />
                      ) : isError ? (
                        <ExclamationCircleIcon className="h-4 w-4" />
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-indigo-600" />
                      )}
                    </span>

                    {/* Step Card Content */}
                    <div className="flex-1 rounded-xl border border-gray-100 bg-slate-50/40 p-3 hover:bg-white hover:border-gray-200 transition-all shadow-2xs">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={classNames(
                            "text-xs font-semibold",
                            isSuccess
                              ? "text-emerald-900"
                              : isError
                              ? "text-rose-900"
                              : "text-slate-800"
                          )}
                        >
                          {log.text}
                        </span>
                        {log.t && (
                          <span className="flex-shrink-0 inline-flex items-center gap-1 font-mono text-[10px] text-gray-400">
                            <ClockIcon className="h-3 w-3" />
                            {moment(log.t).format("HH:mm:ss")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 p-12 text-center text-xs text-gray-400">
              No deployment milestones or progression steps recorded for this app.
            </div>
          )}
        </div>
      )}

      {/* View 2: High-Fidelity Terminal Console */}
      {logView === "terminal" && (
        <div className="overflow-hidden rounded-2xl border border-gray-800 bg-gray-950 shadow-xl">
          {/* macOS-style Header Bar */}
          <div className="flex items-center justify-between border-b border-gray-800 bg-gray-900/90 px-4 py-2.5 text-xs font-mono">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-rose-500/90 shadow-2xs" />
              <span className="h-3 w-3 rounded-full bg-amber-500/90 shadow-2xs" />
              <span className="h-3 w-3 rounded-full bg-emerald-500/90 shadow-2xs" />
              <span className="ml-3 text-gray-400 font-sans text-[11px] font-medium">
                container-stdout · runtime log stream
              </span>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
              <span className="text-emerald-400 font-bold">● Active</span>
              <span>
                {filteredRawLogs.length} / {rawLogs.length} lines
              </span>
            </div>
          </div>

          {/* Terminal Body */}
          <div className="scrollbar max-h-[520px] overflow-y-auto p-4 font-mono text-xs leading-relaxed select-text">
            {filteredRawLogs.length > 0 ? (
              filteredRawLogs.map((log, idx) => {
                let streamColor = "text-gray-300";
                if (log.stream === "cmd")
                  streamColor = "text-cyan-400 font-semibold";
                if (log.stream === "ok")
                  streamColor = "text-emerald-400 font-semibold";
                if (log.stream === "err" || log.stream === "error")
                  streamColor = "text-rose-400 font-medium";
                if (log.stream === "warn")
                  streamColor = "text-amber-400";

                return (
                  <div
                    key={idx}
                    className="flex items-start gap-3 py-0.5 rounded px-1 hover:bg-gray-900/80 transition-colors"
                  >
                    {/* Line Index or Timestamp */}
                    <span className="w-16 flex-shrink-0 select-none text-right text-[11px] text-gray-600 font-mono">
                      {log.t ? moment(log.t).format("HH:mm:ss") : idx + 1}
                    </span>

                    {/* Stream Tag */}
                    <span
                      className={classNames(
                        "w-10 flex-shrink-0 select-none text-center text-[10px] uppercase font-bold tracking-wider rounded px-1 py-0.5",
                        log.stream === "cmd"
                          ? "bg-cyan-950 text-cyan-300 border border-cyan-800"
                          : log.stream === "ok"
                          ? "bg-emerald-950 text-emerald-300 border border-emerald-800"
                          : log.stream === "err"
                          ? "bg-rose-950 text-rose-300 border border-rose-800"
                          : "bg-gray-800 text-gray-400"
                      )}
                    >
                      {log.stream || "out"}
                    </span>

                    {/* Log Text */}
                    <span className={classNames("flex-1 break-all", streamColor)}>
                      {log.text}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="py-12 text-center text-xs text-gray-500 font-mono">
                {rawLogs.length === 0
                  ? "No runtime or build logs available."
                  : `No entries matching "${search}"`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
